// src/pages/Attendance/LeavePage.tsx
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  applyLeave,
  grantCompOff,
  fetchLeaveBalance,
  fetchManagers,
} from "../../store/slices/leaveSlice";
import { RootDispatch, RootState } from "../../store";
import { useEffect } from "react";

export type LeaveSection =
  | "apply"
  | "grant"
  | "pending"
  | "history"
  | "balance";
interface LeavePageProps {
  section: LeaveSection;
}

const LeavePage: React.FC<LeavePageProps> = ({ section }) => {
  const dispatch = useDispatch<RootDispatch>();
  useEffect(() => {
    // dispatch(fetchLeaveBalance());
    dispatch(fetchManagers());
  }, [dispatch]);

  const managerId = useSelector((state: RootState) => state.auth.manager);
  useEffect(() => {
    if (managerId) {
      setFormData((prev) => ({ ...prev, applyingTo: managerId }));
    }
  }, [managerId]);

  useEffect(() => {
    if (section === "grant") {
      setFormData((prev) => ({ ...prev, leaveType: "Comp-Off Grant" }));
    } else {
      setFormData((prev) => ({ ...prev, leaveType: "" }));
    }
  }, [section]);

  useEffect(() => {
    const storedSelection = localStorage.getItem("preSelectedLeaveType");

    if (storedSelection && section === "apply") {
      try {
        const { label } = JSON.parse(storedSelection);
        const validTypes = [
          "Paid Leave",
          "Loss of Pay",
          "Comp-Off",
          "Casual Leave",
          "Sick Leave",
        ];

        if (validTypes.includes(label)) {
          setFormData((prev) => ({ ...prev, leaveType: label }));
        }
      } catch (e) {
        console.error("Error parsing leave type", e);
      } finally {
        localStorage.removeItem("preSelectedLeaveType");
      }
    }
  }, [section]);

  const leaveBalances = useSelector((state: RootState) => state.leave.balances);
  const managers = useSelector((state: RootState) => state.leave.managers);
  const [ccInput, setCcInput] = useState("");
  const [ccDropdownOpen, setCcDropdownOpen] = useState(false);

  const getManagerName = (
    id: string | null,
    managers: { id: string; name: string }[]
  ) => {
    if (!id) return "N/A";
    const manager = managers.find((m) => m.id === id);
    return manager?.name || "N/A";
  };

  const managerName = getManagerName(managerId, managers);

  const [formData, setFormData] = useState({
    leaveType: "",
    fromDate: "",
    fromSession: "Session1",
    toDate: "",
    toSession: "Session2",
    applyingTo: managerId || "Admin",
    ccTo: [] as string[],
    reason: "",
  });

  const leaveTypeMap: Record<string, string> = {
    "Paid Leave": "paid",
    "Sick Leave": "sick",
    "Casual Leave": "casual",
    "Comp-Off": "compOff",
    "Loss of Pay": "lossOfPay",
    "Maternity Leave": "maternity",
    "Paternity Leave": "paternity",
  };

  const leaveTypes =
    section === "grant"
      ? ["Comp-Off Grant"]
      : ["Paid Leave", "Loss of Pay", "Comp-Off", "Casual Leave", "Sick Leave"];

  const sessions = ["Session1", "Session2"];

  const title =
    section === "apply" ? "Apply for Leave" : "Apply for Comp-Off Grant";

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const calculateDays = () => {
    const { fromDate, toDate, fromSession, toSession } = formData;

    if (!fromDate || !toDate) return 0;

    const start = new Date(fromDate);
    const end = new Date(toDate);

    if (start > end) return 0;

    const fromSess = fromSession?.toLowerCase();
    const toSess = toSession?.toLowerCase();

    if (fromDate === toDate) {
      if (fromSess === "session1" && toSess === "session1") return 0.5;
      if (fromSess === "session2" && toSess === "session2") return 0.5;
      if (fromSess === "session1" && toSess === "session2") return 1;
      return 0;
    }

    let diffInDays =
      Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    if (fromSess === "session2") diffInDays -= 0.5;
    if (toSess === "session1") diffInDays -= 0.5;

    return diffInDays;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const {
      leaveType,
      fromDate,
      toDate,
      reason,
      fromSession,
      toSession,
      applyingTo,
    } = formData;
    if (!applyingTo) {
      return toast.error("Manager not loaded yet. Please wait a moment.");
    }

    if (!fromDate || !toDate || !fromSession || !toSession || !reason.trim()) {
      return toast.error("Please fill all required fields.");
    }

    const start = new Date(fromDate);
    const end = new Date(toDate);

    if (start > end) {
      return toast.error("From date cannot be after To date.");
    }

    if (
      fromDate === toDate &&
      fromSession === "Session2" &&
      toSession === "Session1"
    ) {
      return toast.error(
        "Invalid session: Cannot apply from Session 2 to Session 1 on the same day."
      );
    }

    const totalDays = calculateDays();
    if (totalDays <= 0) {
      return toast.error(
        "Invalid leave duration. Please check your dates and sessions."
      );
    }

    if (section === "apply") {
      if (!leaveType) {
        return toast.error("Please select a leave type.");
      }

      const leaveCategory = leaveTypeMap[leaveType];
      if (
        leaveCategory !== "lossOfPay" &&
        (leaveBalances[leaveCategory] ?? 0) < totalDays
      ) {
        return toast.error(
          `Insufficient ${leaveType} balance. You only have ${
            leaveBalances[leaveCategory] || 0
          } day(s) left.`
        );
      }
    }

    let payload;
    if (section === "grant") {
      payload = {
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        fromSession:
          formData.fromSession === "Session1" ? "session1" : "session2",
        toSession: formData.toSession === "Session1" ? "session1" : "session2",
        reason: formData.reason,
        applicationType: "compOffGrant",
        ccTo: formData.ccTo,
        applyingTo: managerId,
      };
    } else {
      payload = {
        category: leaveTypeMap[formData.leaveType] || "casual",
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        fromSession:
          formData.fromSession === "Session1" ? "session1" : "session2",
        toSession: formData.toSession === "Session1" ? "session1" : "session2",
        reason: formData.reason,
        applicationType: "leave",
        applyingTo: formData.applyingTo,
        ccTo: formData.ccTo,
      };
    }

    try {
      if (section === "grant") {
        dispatch(grantCompOff(payload));
      } else {
        dispatch(applyLeave(payload));
      }
      setFormData({
        leaveType: "",
        fromDate: "",
        fromSession: "Session1",
        toDate: "",
        toSession: "Session1",
        applyingTo: "",
        ccTo: [],
        reason: "",
      });
    } catch (error: any) {
      toast.error(error?.message || "Failed to apply for leave");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 max-w-3xl mx-auto my-8 border border-gray-100">
      <h2 className="text-md font-bold mb-6 text-gray-700 border-b pb-3">
        {title}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* First Row */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              LEAVE TYPE
            </label>
            <select
              name="leaveType"
              value={formData.leaveType}
              onChange={handleChange}
              required
              disabled={section === "grant"}
              className={`w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition ${
                section === "grant"
                  ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                  : ""
              }`}
            >
              {section !== "grant" && (
                <option value="">Select Leave Type</option>
              )}
              {leaveTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col items-end">
            <div className="bg-blue-50 rounded-lg p-3 text-right">
              <div className="text-xs text-gray-500">BALANCE</div>
              <div className="text-sm font-medium text-blue-600">
                {formData.leaveType
                  ? `${
                      leaveBalances[leaveTypeMap[formData.leaveType] || ""] || 0
                    } days`
                  : "--"}
              </div>
            </div>
          </div>
        </div>

        {/* Second Row */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              FROM DATE
            </label>
            <input
              type="date"
              name="fromDate"
              value={formData.fromDate}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            />
          </div>

          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              FROM
            </label>
            <select
              name="fromSession"
              value={formData.fromSession}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            >
              {sessions.map((session) => (
                <option key={session} value={session}>
                  {session}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Third Row */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              TO DATE
            </label>
            <input
              type="date"
              name="toDate"
              value={formData.toDate}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            />
          </div>

          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              TO
            </label>
            <select
              name="toSession"
              value={formData.toSession}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            >
              {sessions.map((session) => (
                <option key={session} value={session}>
                  {session}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
          <span className="text-xs text-gray-500">APPLYING FOR</span>
          <span className="text-sm font-medium text-gray-700">
            {calculateDays()} day{calculateDays() !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Applying To */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            APPLYING TO
          </label>
          <input
            type="text"
            value={managerName || "N/A"}
            readOnly
            className="w-full border border-gray-200 bg-gray-100 rounded-lg px-4 py-3 text-sm text-gray-700"
          />
        </div>

        {/* CC TO */}
        <div className="relative">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            CC TO (OPTIONAL)
          </label>

          <input
            type="text"
            value={ccInput}
            onFocus={() => setCcDropdownOpen(true)}
            onBlur={() => setTimeout(() => setCcDropdownOpen(false), 200)}
            onChange={(e) => setCcInput(e.target.value)}
            placeholder="Search manager"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
          />

          {/* Dropdown */}
          {ccDropdownOpen && (
            <div className="absolute bg-white z-10 border rounded-md shadow-lg w-full max-h-48 overflow-y-auto mt-1">
              {managers
                .filter(
                  (m) =>
                    m.name.toLowerCase().includes(ccInput.toLowerCase()) &&
                    !formData.ccTo.includes(m.id) &&
                    m.id !== managerId
                )
                .map((manager) => (
                  <div
                    key={manager.id}
                    onMouseDown={() => {
                      setFormData((prev) => ({
                        ...prev,
                        ccTo: [...prev.ccTo, manager.id],
                      }));
                      setCcInput("");
                    }}
                    className="px-4 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                  >
                    {manager.name}
                  </div>
                ))}
            </div>
          )}

          {/* Selected Tags */}
          <div className="mt-2 flex flex-wrap gap-2">
            {formData.ccTo.map((id) => {
              const mgr = managers.find((m) => m.id === id);
              return (
                <span
                  key={id}
                  className="flex items-center bg-gray-100 text-sm text-gray-700 px-3 py-1 rounded-full"
                >
                  {mgr?.name || "Unknown"}
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        ccTo: prev.ccTo.filter((ccId) => ccId !== id),
                      }))
                    }
                    className="ml-2 text-gray-500 hover:text-red-500"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </div>

        {/* Reason */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            REASON
          </label>
          <textarea
            name="reason"
            value={formData.reason}
            onChange={handleChange}
            rows={3}
            required
            placeholder="Please specify the reason for leave"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
          />
        </div>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-medium rounded-lg shadow-md hover:from-blue-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
          >
            Submit Application
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeavePage;
